'use client';

import { addToast } from '@heroui/react';
import { mapboxPlacesAction } from './mapboxPlacesAction';

/**
 * Reusable function to call the Mapbox Places API with consistent error handling
 * @param address - The address string to search for
 * @param options - Optional configuration
 * @returns Promise<any[]> - Array of place features or empty array on error
 */
export async function callMapboxPlaces(
  address: string,
  options: {
    showErrorToasts?: boolean;
    silentMode?: boolean;
    accessToken?: string;
    proximity?: { lat: number; lng: number };
    types?: 'address' | 'postcode' | 'address,postcode';
  } = {}
): Promise<any[]> {
  const {
    showErrorToasts = true,
    silentMode = false,
    accessToken,
    proximity,
    types = 'address',
  } = options;

  try {
    const result = await mapboxPlacesAction(
      address,
      accessToken,
      proximity,
      types
    );

    if (result.error) {
      throw new Error(result.error);
    }

    return result.features ?? [];
  } catch (error) {
    if (!silentMode) {
      console.error('Error searching addresses:', error);
    }

    if (showErrorToasts) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Show user-friendly error message
      if (errorMessage === 'Unauthorized') {
        addToast({
          title: 'Address Search Unavailable',
          description:
            'Address search service is temporarily unavailable. Please enter your address manually.',
          color: 'warning',
        });
      } else if (errorMessage === 'Geocoding service temporarily unavailable') {
        addToast({
          title: 'Service Temporarily Unavailable',
          description:
            'Address search is temporarily down. Please try again later.',
          color: 'warning',
        });
      } else {
        addToast({
          title: 'Search Error',
          description: 'Unable to search addresses. Please try again.',
          color: 'danger',
        });
      }
    }

    return [];
  }
}

/**
 * Geocode an address and return coordinates
 * @param address - The full address string to geocode
 * @param options - Optional configuration
 * @param propertyData - Optional property data to validate zipcode match
 * @returns Promise with coordinates or null
 */
export async function geocodeAddress(
  address: string,
  options: {
    showErrorToasts?: boolean;
    silentMode?: boolean;
    accessToken?: string;
    proximity?: { lat: number; lng: number };
    propertyData?: {
      zip?: string;
    } | null;
    types?: 'address' | 'postcode' | 'address,postcode';
    /**
     * Only accept a match that carries a house number. Mapbox answers an
     * unknown house number with the bare street, which would otherwise pass for
     * the building.
     */
    requireHouseNumber?: boolean;
  } = {}
): Promise<{ lat: number; lng: number } | null> {
  const features = await callMapboxPlaces(address, {
    showErrorToasts: options.showErrorToasts,
    silentMode: options.silentMode,
    accessToken: options.accessToken,
    proximity: options.proximity,
    types: options.types,
  });

  if (features.length === 0) {
    return null;
  }

  // Filter features by zipcode if propertyData.zip is provided
  let validFeatures = features;
  if (options.propertyData?.zip) {
    const expectedZip = options.propertyData.zip.trim();

    validFeatures = features.filter((feature: any) => {
      // For postcode-type results, the zip is in the text field directly
      const isPostcode = feature.place_type?.includes('postcode');
      if (isPostcode) {
        return feature.text?.trim() === expectedZip;
      }

      // For address-type results, find postcode in context array
      const postcodeContext = feature.context?.find((ctx: any) =>
        ctx.id?.startsWith('postcode.')
      );

      if (postcodeContext) {
        const featureZip = postcodeContext?.text?.trim();
        return featureZip === expectedZip;
      }
      return false;
    });
  }

  if (options.requireHouseNumber) {
    validFeatures = validFeatures.filter((feature: any) => !!feature.address);
  }

  if (validFeatures.length > 0 && validFeatures[0].center) {
    return {
      lat: validFeatures[0].center[1],
      lng: validFeatures[0].center[0],
    };
  }

  return null;
}
